import { Component, Inject, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { NavigationStart, Router } from '@angular/router';
import { equals } from 'ramda';
import { combineLatest, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { select } from '@angular-redux/store';
import { OtpModalService } from '@tango/app/core/otp-modal.service';
import {
  ApiService,
  AppVersionCheckerService,
  LocalizationService,
  MobileDetectService,
  NavigatorService,
} from '@tango/app/core/services';
import { MobileDetectActions } from '@tango/app/core/actions';
import { localizations } from '../localizations';
import { APP_CONFIG } from '@tango/app/app.config';
import { AppConfig } from '@tango/app/interfaces';
import { IconsService } from '@tango/app/core/icons.service';
import { IdleService } from '@tango/app/core/idle.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
})
export class AppComponent implements OnInit {
  @select(['notifications', 'newNotificationsCount', 'count'])
  notificationsCount$: Observable<number>;

  @select(['auth', 'isLoggedIn'])
  isLoggedIn$: Observable<boolean>;

  hasNotificationsBtn$: Observable<boolean>;

  constructor(
    icons: IconsService,
    private titleService: Title,
    private appVersionChecker: AppVersionCheckerService,
    @Inject(APP_CONFIG) private config: AppConfig,
    private api: ApiService,
    private mobileDetectService: MobileDetectService,
    private mobileDetectActions: MobileDetectActions,
    private localizationService: LocalizationService,
    private translate: TranslateService,
    private otpModalService: OtpModalService,
    private goto: NavigatorService,
    private router: Router,
    protected idleService: IdleService
  ) {
    translate.setDefaultLang('uk');
    translate.use(this.localizationService.currentLocale);
    icons.init();
  }

  ngOnInit() {
    this.configure();
    this.appVersionChecker.checkActuality();
    this.mobileDetectService.initViewportDetection();

    this.mobileDetectService.isXlDesktop$
      .pipe(filter(equals(true)))
      .subscribe(() => {
        // Для сохранения совместимости также устанавливаем Desktop.
        this.mobileDetectActions.detectDesktop();
        this.mobileDetectActions.detectXlDesktop();
      });

    this.mobileDetectService.isDesktop$
      .pipe(filter(equals(true)))
      .subscribe(() => this.mobileDetectActions.detectDesktop());

    this.mobileDetectService.isTablet$
      .pipe(filter(equals(true)))
      .subscribe(() => this.mobileDetectActions.detectTablet());

    this.mobileDetectService.isMobile$
      .pipe(filter(equals(true)))
      .subscribe(() => this.mobileDetectActions.detectMobile());

    this.localizationService.locale$.subscribe(this.setTitle);
    this.otpModalService.init();
    this.idleService.init();

    this.hasNotificationsBtn$ = this.setHasNotificationsBtn();
  }

  onMainFabBtnClick() {
    this.goto.notifications();
  }

  l10n(fieldName: string): string {
    return this.localizationService.getField(fieldName, 'app');
  }

  configure() {
    this.localizationService.setLocalizations(localizations);
  }

  setTitle = () => {
    const locale = this.localizationService.currentLocale;
    this.titleService.setTitle(
      this.config.bankName[locale] + ' ' + this.l10n('title')
    );
  };

  protected setHasNotificationsBtn(): Observable<boolean> {
    const isNotificationsRoute$ = this.router.events.pipe(
      filter((e) => e instanceof NavigationStart),
      map((e: any) => e.url === '/notifications')
    );

    return combineLatest([this.isLoggedIn$, isNotificationsRoute$]).pipe(
      map(
        ([isLoggedIn, isNotificationsRoute]) =>
          isLoggedIn && !isNotificationsRoute
      )
    );
  }
}
